import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { GraduationCap, Home, ListChecks, FileText, BarChart3, LogOut, Crown, Zap, Shield, AlertTriangle, ChevronDown, Brain, BookOpen, TrendingUp, Target, Headphones } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { NAV } from "@/constants/testIds/vc";
import { PREMIUM } from "@/constants/testIds/premium";

const primary = [
  { to: "/inicio", label: "Início", icon: Home, tid: NAV.home, end: true },
  { to: "/questoes", label: "Questões", icon: ListChecks, tid: NAV.questoes },
  { to: "/simulados", label: "Simulados", icon: FileText, tid: NAV.simulados },
  { to: "/desempenho", label: "Desempenho", icon: BarChart3, tid: NAV.desempenho },
  { to: "/erros", label: "Erros", icon: AlertTriangle, tid: "nav-erros" },
];

const secondary = [
  { to: "/incidencia", label: "Incidência", icon: TrendingUp, tid: "nav-incidencia" },
  { to: "/mapas-mentais", label: "Mapas Mentais", icon: Brain, tid: "nav-mapas" },
  { to: "/materiais", label: "Materiais", icon: BookOpen, tid: "nav-materiais" },
  { to: "/plano", label: "Plano de Estudos", icon: Target, tid: "nav-plano" },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
    isActive ? "bg-emerald-500/15 text-emerald-400" : "text-slate-300 hover:bg-slate-800 hover:text-white"
  }`;

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const premium = user?.is_premium;

  return (
    <header translate="no" className="notranslate sticky top-0 z-40 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
        <button onClick={() => navigate("/inicio")} className="flex items-center transition-opacity hover:opacity-90" data-testid="nav-logo">
          <img
            src="/images/logo-apoiaconcursos.jpeg"
            alt="ApoiaConcursos"
            className="h-12 w-auto rounded-lg object-contain"
          />
        </button>

        <nav className="hidden items-center gap-1 lg:flex">
          {primary.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.tid} className={linkClass}>
              <l.icon className="h-4 w-4" /> {l.label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-estudar" className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-slate-300 outline-none transition-colors hover:bg-slate-800 hover:text-white">
              <Brain className="h-4 w-4" /> Estudar <ChevronDown className="h-3.5 w-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-slate-700 bg-slate-900 text-slate-200">
              {secondary.map((l) => (
                <DropdownMenuItem key={l.to} data-testid={l.tid} onClick={() => navigate(l.to)}
                  className="flex cursor-pointer items-center gap-2 focus:bg-slate-800 focus:text-emerald-400">
                  <l.icon className="h-4 w-4" /> {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink
            to="/suporte"
            data-testid="nav-suporte"
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-emerald-500/15 text-emerald-400"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <Headphones className="h-4 w-4" /> Suporte
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin"
              className={({ isActive }) => `flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-amber-500/15 text-amber-400" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
              <Shield className="h-4 w-4" /> Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {premium ? (
            <span data-testid={PREMIUM.activeBadge} className="flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-bold text-amber-300">
              <Crown className="h-3.5 w-3.5" /> PREMIUM ATIVO
            </span>
          ) : (
            <button data-testid={PREMIUM.navButton} onClick={() => navigate("/premium")}
              className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-3.5 py-1.5 text-xs font-bold text-slate-950 shadow-lg transition-transform hover:scale-105 active:scale-95">
              <Zap className="h-3.5 w-3.5" /> Teste Premium
            </button>
          )}
          <button data-testid={NAV.logout} onClick={() => { logout(); navigate("/login"); }}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-800 hover:text-white" title="Sair">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* mobile nav — scrollable, includes all areas */}
      <nav className="flex items-center gap-1 overflow-x-auto border-t border-slate-800 px-2 py-1 lg:hidden">
        {[...primary, ...secondary].map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={`m-${l.tid}`}
            className={({ isActive }) => `flex shrink-0 flex-col items-center gap-0.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${isActive ? "text-emerald-400" : "text-slate-400"}`}>
            <l.icon className="h-4 w-4" /> {l.label}
          </NavLink>
        ))}
        <NavLink
          to="/suporte"
          data-testid="m-nav-suporte"
          className={({ isActive }) =>
            `flex shrink-0 flex-col items-center gap-0.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${
              isActive ? "text-emerald-400" : "text-slate-400"
            }`
          }
        >
          <Headphones className="h-4 w-4" /> Suporte
        </NavLink>
        {user?.role === "admin" && (
          <NavLink to="/admin" className={({ isActive }) => `flex shrink-0 flex-col items-center gap-0.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold ${isActive ? "text-amber-400" : "text-slate-400"}`}>
            <Shield className="h-4 w-4" /> Admin
          </NavLink>
        )}
      </nav>
    </header>
  );
};
